import React, { useEffect, useRef, useState } from 'react';
import type { ChatSessionDto } from '../../types/api';

interface ChatSessionListProps {
  sessions: ChatSessionDto[];
  activeSessionId: string | null;
  /** Session ids with an in-flight assistant reply (API flag + live client state). */
  generatingSessionIds?: ReadonlySet<string>;
  loading?: boolean;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onRename: (sessionId: string, title: string) => Promise<void> | void;
  onDelete: (sessionId: string) => Promise<void> | void;
  /** Mobile-only close button (hidden on >= md). */
  onClose?: () => void;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ChatSessionList: React.FC<ChatSessionListProps> = ({
  sessions,
  activeSessionId,
  generatingSessionIds,
  loading,
  onSelect,
  onNew,
  onRename,
  onDelete,
  onClose,
}) => {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (renameId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameId]);

  useEffect(() => {
    if (!menuOpenId) return;
    const close = () => setMenuOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpenId]);

  const startRename = (session: ChatSessionDto) => {
    setMenuOpenId(null);
    setRenameId(session.sessionId);
    setRenameValue(session.title);
  };

  const submitRename = async (sessionId: string) => {
    const title = renameValue.trim();
    setRenameId(null);
    if (!title) return;
    await onRename(sessionId, title);
  };

  return (
    <aside className="h-full flex flex-col bg-surface-container-low border-r border-outline">
      <div className="shrink-0 p-3 border-b border-outline flex items-center gap-2">
        <button
          type="button"
          onClick={onNew}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-semibold border border-primary/20"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          <span>New chat</span>
        </button>
        {onClose && (
          <button
            type="button"
            aria-label="Close session list"
            onClick={onClose}
            className="md:hidden p-2 rounded-lg hover:bg-elevation-dark text-slate-200"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-2 space-y-1">
        {loading && sessions.length === 0 && (
          <div className="text-center text-slate-400 text-xs py-6">Loading...</div>
        )}
        {!loading && sessions.length === 0 && (
          <div className="text-center text-slate-400 text-xs py-6 px-4">
            No conversations yet. Start one with the button above.
          </div>
        )}
        {sessions.map((s) => {
          const active = s.sessionId === activeSessionId;
          const isRenaming = renameId === s.sessionId;
          const isGenerating =
            generatingSessionIds?.has(s.sessionId) ?? s.hasPendingReply ?? false;
          return (
            <div
              key={s.sessionId}
              className={`group relative rounded-lg border transition-colors ${
                active
                  ? 'bg-primary/10 border-primary/30'
                  : 'bg-transparent border-transparent hover:bg-surface-container-high hover:border-outline'
              }`}
            >
              {isRenaming ? (
                <input
                  ref={renameInputRef}
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => submitRename(s.sessionId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitRename(s.sessionId);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setRenameId(null);
                    }
                  }}
                  maxLength={200}
                  className="w-full bg-surface-container text-on-surface text-sm rounded-lg px-3 py-2 border border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => onSelect(s.sessionId)}
                    className="w-full text-left px-3 py-2 pr-9"
                  >
                    <p
                      className={`text-sm truncate flex items-center gap-1.5 ${
                        active ? 'text-primary font-semibold' : 'text-slate-200 font-medium'
                      }`}
                    >
                      {isGenerating && (
                        <span
                          className="material-symbols-outlined text-sm animate-spin text-primary shrink-0"
                          aria-hidden
                        >
                          sync
                        </span>
                      )}
                      <span className="truncate">{s.title}</span>
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                      {relativeTime(s.updatedAt)} {s.messageCount > 0 && `\u00b7 ${s.messageCount} msg`}
                    </p>
                  </button>
                  <button
                    type="button"
                    aria-label="Session options"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(menuOpenId === s.sessionId ? null : s.sessionId);
                    }}
                    className={`absolute top-1.5 right-1.5 p-1.5 rounded-md text-slate-400 hover:bg-elevation-dark hover:text-slate-200 transition-colors ${
                      menuOpenId === s.sessionId ? 'bg-elevation-dark text-slate-200' : ''
                    }`}
                  >
                    <span className="material-symbols-outlined text-base">more_vert</span>
                  </button>
                  {menuOpenId === s.sessionId && (
                    <div
                      role="menu"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-1 top-9 z-10 w-36 bg-surface-container-high border border-outline rounded-lg shadow-lg overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => startRename(s)}
                        className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-elevation-dark flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-base">edit</span>
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          setMenuOpenId(null);
                          if (window.confirm(`Delete "${s.title}"?`)) {
                            await onDelete(s.sessionId);
                          }
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-base">delete</span>
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default ChatSessionList;
